import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useBulkQuickSetStock } from '../../hooks';
import { useProductLocationLookup } from '../../lib/useProductLocationLookup';
import type { ToastSeverity } from '../../lib/useToast';

/** Inventory adjustments name every product explicitly — mirror the BFF's per-call cap. */
const MAX_BULK_ITEMS = 100;

interface BulkStockUpdateDialogProps {
  open: boolean;
  selectedCount: number;
  totalCount: number;
  selectAllMode: boolean;
  selectedIds: Set<string>;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/**
 * Bulk SET-TO-VALUE stock edit for an EXPLICIT selection (plan-catalog-007 Phase 2) — one
 * location, one counted quantity for every selected product, applied by the BFF as per-product
 * MANUAL_CORRECTION inventory adjustments (ledger-audited; unchanged products are skipped).
 *
 * Filter-based "select all" is deliberately NOT supported any more: every ledger entry names its
 * product, so an unbounded server-side filter application (the old master bulk write) has no
 * inventory equivalent — the dialog explains and asks for an explicit selection instead.
 */
export function BulkStockUpdateDialog({
  open,
  selectedCount,
  totalCount,
  selectAllMode,
  selectedIds,
  onClose,
  onResult,
}: BulkStockUpdateDialogProps) {
  const { t } = useTranslation('catalog');
  const update = useBulkQuickSetStock();
  const { locations, isLoading: locationsLoading } = useProductLocationLookup();
  const activeLocations = useMemo(() => locations.filter((l) => l.active), [locations]);

  const [locationId, setLocationId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setLocationId(activeLocations.length === 1 ? activeLocations[0].id : '');
      setQuantity('');
      setNote('');
    }
  }, [open, activeLocations]);

  const parsedQuantity = quantity.trim() === '' ? undefined : Number(quantity);
  const quantityValid =
    parsedQuantity !== undefined && !Number.isNaN(parsedQuantity) && parsedQuantity >= 0;
  const overLimit = selectedIds.size > MAX_BULK_ITEMS;
  const submittable =
    !selectAllMode && !overLimit && selectedIds.size > 0 && Boolean(locationId) && quantityValid;

  const submit = () => {
    if (!submittable) return;
    update.mutate(
      {
        locationId,
        items: Array.from(selectedIds).map((productId) => ({
          productId,
          newQuantity: parsedQuantity as number,
        })),
        note: note.trim() || undefined,
      },
      {
        onSuccess: (result) => {
          onResult(
            result.failedCount > 0 ? 'error' : 'success',
            result.failedCount > 0
              ? t('bulkStockDialog.successWithFailures', {
                  updated: result.updatedCount,
                  skipped: result.skippedCount,
                  failed: result.failedCount,
                })
              : t('bulkStockDialog.success', {
                  updated: result.updatedCount,
                  skipped: result.skippedCount,
                }),
          );
          onClose();
        },
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('bulkStockDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {selectAllMode ? (
            <Alert severity="warning">
              {t('bulkStockDialog.selectAllUnsupported', { total: totalCount })}
            </Alert>
          ) : overLimit ? (
            <Alert severity="warning">
              {t('bulkStockDialog.overLimit', { max: MAX_BULK_ITEMS, count: selectedIds.size })}
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('bulkStockDialog.selectedCount', { count: selectedCount })}
            </Typography>
          )}
          <TextField
            select
            required
            label={t('bulkStockDialog.locationLabel')}
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            disabled={locationsLoading || selectAllMode}
            helperText={
              activeLocations.length === 0 && !locationsLoading
                ? t('bulkStockDialog.noLocations')
                : undefined
            }
            fullWidth
          >
            {activeLocations.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.name} · {l.code}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            required
            label={t('bulkStockDialog.quantityLabel')}
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            helperText={t('bulkStockDialog.quantityHelper')}
            error={quantity.trim() !== '' && !quantityValid}
            disabled={selectAllMode}
            fullWidth
            slotProps={{ htmlInput: { min: 0, step: 1 } }}
          />
          <TextField
            label={t('bulkStockDialog.noteLabel')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={selectAllMode}
            fullWidth
          />
          {!selectAllMode ? (
            <Alert severity="info" variant="outlined">
              {t('bulkStockDialog.ledgerHint')}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={update.isPending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={update.isPending || !submittable}
          startIcon={update.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
